import {
  LoginCred,
  useAuthGoogleGetAuthorizationMutation,
  useAuthLoginGoogleMutation,
  useAuthLoginMutation,
  useLazyAuthWhoamiQuery,
} from '@app/services/auto-generated/auth'
import {
  Alert,
  Button,
  Container,
  Divider,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@common/components'
import {
  DASHBOARD_ROUTE,
  FORGET_ROUTE,
  MY_CLASSROOMS_ROUTE,
  PROFILE_ROUTE,
  REGISTER_ROUTE,
  VERIFY_ROUTE,
} from '@common/constants/routes'
import { useTranslation } from '@common/hooks'
import { EmailIcon, GoogleIcon, KeyIcon } from '@common/icons'
import { yupResolver } from '@hookform/resolvers/yup'
import { SubmitHandler, useForm } from '@lib/form'
import { RouterLink, useNavigate, useSearchParams, useLocation } from '@lib/router'
import { isErrorResponse } from '@types'
import { urlEncodeEmail } from '@utils'
import React from 'react'
import { loginSchema } from '@common/utils/validations/auth.validations'
import { StyledTextField } from './login-form.styles'
import { useMediaQuery } from '@common/hooks'

const LoginForm: React.FC = () => {
  const isMobile = useMediaQuery(950)
  const [searchParams] = useSearchParams()
  const { t } = useTranslation('login-page')
  const navigate = useNavigate()
  const location = useLocation()

  const { handleSubmit, control, getValues } = useForm<LoginCred>({
    defaultValues: {
      password: '',
      email: '',
      username: '',
    },
    resolver: yupResolver(loginSchema),
  })

  const parseJSON = (str: string) => {
    try {
      return JSON.parse(str)
    } catch (err) {
      return null
    }
  }

  const [login, { isLoading: isLoadingLogin, isError }] = useAuthLoginMutation()
  const [
    getGoogleAuth,
    { isLoading: isLoadingGetGoogleAuth, isSuccess: isSuccessGoogleAuthLogin },
  ] = useAuthGoogleGetAuthorizationMutation()
  const [loginWithGoogle, { isLoading: isLoadingLoginWithGoogle }] = useAuthLoginGoogleMutation()
  const [getUser] = useLazyAuthWhoamiQuery()

  const onSubmit: SubmitHandler<LoginCred> = async (formState) => {
    try {
      if (formState.email === '') {
        formState.email = null
      }
      if (formState.username === '') {
        formState.username = null
      }

      try {
        await login({ loginCred: formState }).unwrap()
      } catch (err: any) {
        if (isErrorResponse(err)) {
          if (err.data.detail.error_code === 'EXC_USER_NOT_VERIFIED') {
            const email: string | undefined = getValues('email')
            if (email) {
              navigate(`${VERIFY_ROUTE}?email=${urlEncodeEmail(email)}`)
            }
          }
        }
      }
      await getUser().unwrap()
    } catch (err) {}
    if (formState.username === null) {
      formState.username = ''
    }
    if (formState.email === null) {
      formState.email = ''
    }
  }

  const handleGoogleLogin = async () => {
    try {
      const previousUrl = location.state?.from?.pathname || DASHBOARD_ROUTE
      console.log('previousUrl:', previousUrl)
      const state = encodeURIComponent(JSON.stringify({ realRedirectUrl: previousUrl }))
      const { url } = await getGoogleAuth({
        googleGetAuthorizationRequest: {
          redirect_url: `${window.location.origin}/`,
          state,
        },
      }).unwrap()
      window.location.href = url
    } catch (error) {
      console.error('Google login error:', error)
    }
  }

  // for google sign in
  const code = searchParams.get('code')
  const state = searchParams.get('state')

  const loginGoogleFlow = async () => {
    if (code) {
      await loginWithGoogle({
        googleLoginRequest: {
          code,
          redirect_url: `${window.location.origin}/`,
          ext: state ? parseJSON(decodeURIComponent(state)) : null,
        },
      }).unwrap()
      await getUser().unwrap()
      const parsedState = parseJSON(decodeURIComponent(state))
      if (parsedState && parsedState['realRedirectUrl'] === PROFILE_ROUTE) {
        navigate(MY_CLASSROOMS_ROUTE)
        return
      }
      if (parsedState) {
        window.location.href = parsedState['realRedirectUrl']
      }
    }
  }

  React.useEffect(() => {
    if (code) {
      loginGoogleFlow()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  // if (isLoadingLoginWithGoogle) {
  //   return (
  //     <Box
  //       sx={{
  //         width: '300px',
  //         height: '300px',
  //         display: 'flex',
  //         justifyContent: 'center',
  //         alignItems: 'center',
  //       }}
  //     >
  //       <CircularProgress color='error' />
  //     </Box>
  //   )
  // }

  return (
    <Container component='form' maxWidth='xs' onSubmit={handleSubmit(onSubmit)}>
      <Typography align='center' sx={{ mb: 4, fontSize: { xs: '17px', sm: '20px', md: '24px' } }}>
        {t('title', 'Returning users')}
      </Typography>
      {isError && (
        <Alert variant='outlined' severity='error' sx={{ mb: 2 }}>
          Please verify your email and password
        </Alert>
      )}
      <StyledTextField
        size={isMobile ? 'small' : 'medium'}
        name='email'
        type='email'
        control={control}
        placeholder={t('email-input.placeholder', 'Email')}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <EmailIcon />
            </InputAdornment>
          ),
        }}
        fullWidth
        sx={{ mb: 2 }}
      />
      <StyledTextField
        size={isMobile ? 'small' : 'medium'}
        name='password'
        type='password'
        control={control}
        placeholder={t('password-input.placeholder', 'Password')}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <KeyIcon />
            </InputAdornment>
          ),
        }}
        fullWidth
        sx={{ mb: 1 }}
      />
      <Typography variant='body2' sx={{ mb: 2 }}>
        {t('forget-password.typo', 'Forgot your password?')}{' '}
        <Link underline='hover' component={RouterLink} to={FORGET_ROUTE}>
          {t('forget-password.link', 'Reset')}
        </Link>
      </Typography>
      <Button
        size={isMobile ? 'small' : 'medium'}
        loading={isLoadingLogin}
        variant='contained'
        type='submit'
        fullWidth
        sx={{ mb: 2 }}
      >
        {t('submit-button.label', 'Login')}
      </Button>
      <Divider sx={{ my: 1 }}>OR</Divider>
      <Button
        fullWidth
        onClick={handleGoogleLogin}
        variant='outlined'
        color='neutral'
        loading={isLoadingGetGoogleAuth}
        startIcon={<GoogleIcon />}
        sx={{ mb: 1 }}
        size={isMobile ? 'small' : 'medium'}
      >
        {t('google-button.label', 'Sign in with google')}
      </Button>
      <Typography variant='body2' sx={{ mt: 1 }}>
        {t('no-account.typo', "Don't have an account?")}{' '}
        <Link underline='hover' component={RouterLink} to={REGISTER_ROUTE + '?step=0'}>
          {t('no-account.link', 'Register')}
        </Link>
      </Typography>
    </Container>
  )
}

export default LoginForm
